import { DatePicker } from '@civilgrid/ui/components';
import { isSingleDatePickerValue } from '@civilgrid/ui/DatePicker/utils';
import type { Table } from '@tanstack/react-table';
import type React from 'react';
import { useEffect } from 'react';
import Select, { type Item as SelectItem } from '~/components2/forms/Select';
import type FilterGroup from './Filters/FilterGroup';
import type { FilterGroupProps } from './Filters/FilterGroup';
import type FilterRule from './Filters/FilterRule';
import type { FilterRuleProps } from './Filters/FilterRule';
import Button from './Legacy/LegacyButton';
import store from './store';

export type TFilterCondition = 'and' | 'or';
export type TFilterValue = string | boolean | Date;
export type TTableFilter = FilterGroup | FilterRule;
export type TTableFilterProps = FilterRuleProps | FilterGroupProps;
export type TShortcut = {
  label: React.ReactNode;
  filterState: FilterProperties[];
};

export type FilterRuleProperties = Pick<FilterRule, 'id' | 'columnId' | 'operator' | 'value' | 'type'>;

interface WithChildren extends Omit<FilterGroup, 'children'> {
  children?: FilterProperties[];
}

export type FilterGroupProperties = Pick<WithChildren, 'id' | 'type' | 'condition' | 'children'>;

export type FilterProperties = FilterRuleProperties | FilterGroupProperties;

export enum FilterOperator {
  Includes = 'includes',
  DoesNotInclude = 'doesNotInclude',
  IsEqualTo = 'isEqualTo',
  IsNotEqualTo = 'isNotEqualTo',
  IsEmpty = 'isEmpty',
  IsNotEmpty = 'isNotEmpty',
  IsBefore = 'isBefore',
  IsAfter = 'isAfter',
  IsTrue = 'isTrue',
  IsFalse = 'isFalse',
  IsNull = 'isNull',
}

interface SimpleColumn {
  type: 'text';
}

interface BooleanColumn {
  type: 'boolean';
}

interface SelectColumn {
  type: 'select';
  items: SelectItem[];
}

interface DateColumn {
  type: 'date';
}

export type AdvancedFilterColumnDef = { id: string } & (SimpleColumn | SelectColumn | DateColumn | BooleanColumn);

export interface TableFilterPropsBase<T> {
  table: Table<T>;
  columns: AdvancedFilterColumnDef[];
  parentId?: string;
  onQuery?: (filterTree: FilterGroup, searchParams: { [key: string]: any }) => void;
}

export const conditions: SelectItem[] = [
  { value: 'and', label: <div className="text-xs">And</div> },
  { value: 'or', label: <div className="text-xs">Or</div> },
];

// DISABLING this function for now, for
// export function updateTable<T>(...

const booleanOperators = new Set([FilterOperator.IsTrue, FilterOperator.IsFalse]);
const emptyOperators = new Set([FilterOperator.IsEmpty, FilterOperator.IsNotEmpty, FilterOperator.IsNull]);
const dateOperators = new Set([FilterOperator.IsBefore, FilterOperator.IsAfter]);

export function isBooleanOperator(operator: FilterOperator): boolean {
  return booleanOperators.has(operator);
}

export function isEmptyStateOperator(operator: FilterOperator): boolean {
  return emptyOperators.has(operator);
}

export function isDateOperator(operator: FilterOperator): boolean {
  return dateOperators.has(operator);
}

export interface RemoveFilterProps<T> extends TableFilterPropsBase<T> {
  filterId: string;
}

export function RemoveFilter<T>({ filterId, parentId }: RemoveFilterProps<T>) {
  if (!parentId) return null;

  return (
    <Button
      ghost
      icon="close"
      aria-label="Remove filter"
      data-testid={`advanced-filter-remove-${filterId}`}
      onClick={(e) => {
        store.removeFilter(filterId, parentId);
      }}
    />
  );
}

export interface ConditionProps<T> extends TableFilterPropsBase<T> {
  filter: FilterGroup;
}

export function Condition<T>({ parentId, filter }: ConditionProps<T>) {
  if (!parentId) return null;

  return (
    <div className="w-[5rem] grow-0 text-sm">
      <Select
        label=""
        value={filter.condition}
        items={conditions}
        onChange={(e) => {
          if (!e) return;
          const v = e as TFilterCondition;
          (store.filtersById[filter.id] as FilterGroup)?.setCondition(v);
        }}
        popoverTriggerClassName="w-full min-w-0!"
      />
    </div>
  );
}

export function ClearFilters<T>({ parentId, onQuery }: TableFilterPropsBase<T>) {
  if (parentId) return null;

  return (
    <Button
      alternate
      onClick={() => {
        store.reset();
        const t = store.filterTree;
        onQuery?.(t, store.getFiltersAsSearchParams(t));
      }}
    >
      Clear Filters
    </Button>
  );
}

export interface ShortcutsProps<T> extends Pick<TableFilterPropsBase<T>, 'columns' | 'table'> {
  shortcuts?: TShortcut[];
}

export function Shortcuts<T>({ columns, shortcuts = [] }: ShortcutsProps<T>) {
  if (!shortcuts?.length || !columns?.[0]) return null;

  return (
    <div>
      <br />
      <hr />
      <br />
      <div className="mb-2 font-medium text-xs">Shortcuts</div>
      <div className="grid grid-cols-3 gap-2">
        {shortcuts.map((s) => {
          return (
            <Button
              key={`shortcut-${s.label}`}
              alternate
              onClick={() => {
                store.reset();
                store.buildFilterTreeFromProps(s.filterState, 'root');
              }}
            >
              {s.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export interface DateSelectProps {
  rule: FilterRule;
}

export function DateSelect({ rule }: DateSelectProps) {
  let value: Date | null = null;

  const d = new Date(rule.value?.toString() ?? '');
  if (!Number.isNaN(d.getFullYear())) {
    value = d;
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const d = new Date(rule.value?.toString() ?? '');
    if (Number.isNaN(d.getFullYear())) {
      const f = store.filtersById[rule.id] as FilterRule;
      f.setValue(new Date().toString());
    }
  }, [rule.id]);

  return (
    <div className="relative flex grow flex-col mt-1">
      <DatePicker
        value={value ? { date: value.toISOString(), view: 'Day' } : undefined}
        onChange={(value) => {
          const f = store.filtersById[rule.id] as FilterRule;
          if (isSingleDatePickerValue(value)) {
            f.setValue(value.date.toString());
          } else {
            f.setValue('');
          }
        }}
        placeholder="Select date"
        className="w-full bg-white!"
      />
    </div>
  );
}
